/** import package [start] */
import { FC, useEffect, useState, createRef } from 'react';
import { Modal } from '@zeeve-platform/modal-component';
import { Breadcrumb } from '@zeeve-platform/breadcrumb-component';
import axios from 'axios';
/** import package [end] */

/** import custom dependency [start] */
// import axios from 'axios';
import useSidebarStore from '../../store/sidebar';
import BaseUrls from '../../utils/baseUrls';
import Loader from '../../components/Loader';
import { ProtocolInfo } from '../../types/node';
import { getPlanData, createSubscription } from '../../services/node';
import Shardeumlogo from '../../assets/shardeum_dark.svg';
import CloudSelect, { IFRef as CSIFRef } from './components/CloudSelect';
import NodeType, { IFRef as NTIFRef } from './components/NodeType';
import Nodes, { IFRef as NIFRef } from './components/Nodes';
/** import custom dependency [end] */

/** type declaration of the Purchase Node details props */
export interface PurchaseNodeProps {
    purchaseType: 'dedicated-node' | 'staking-node';
}

/**
 * Purchase Node page
 * @returns JSX.Element
 */
const PurchaseNode: FC<PurchaseNodeProps> = ({ purchaseType }): JSX.Element => {

    // misc states
    const [isLoading, setLoading] = useState<boolean>(false);
    const breadcrumbPath = [
        { name: 'Dashboard', path: BaseUrls.getPlatformDashboardUrl() },
        {
            name: purchaseType === 'dedicated-node' ? 'Dedicated Full Nodes' : 'Dedicated Validator Nodes',
            path:
                purchaseType === 'dedicated-node'
                    ? BaseUrls.getPlatformManageDedicatedNodesUrl()
                    : BaseUrls.getPlatformManageStakingNodesUrl(),
        },
        {
            name: 'Select Subscription',
            path:
                purchaseType === 'dedicated-node'
                    ? BaseUrls.getPlatformBuyDedicatedNodesUrl()
                    : BaseUrls.getPlatformBuyStakingNodesUrl(),
        },
    ];
    const [componentError, setComponentError] = useState<string | null>(null);

    //compoenent dependent states
    const [protocolData, setProtocolData] = useState<ProtocolInfo>({} as ProtocolInfo);
    const [selectedCloud, setSelectedCloud] = useState<string>('ZEEVE_MANAGED');
    const [nodeCount, setNodeCount] = useState<number>(1);
    const [nodeType, setNodeType] = useState<string>('');
    const [nodeCost, setNodeCost] = useState<number>(0);
    const [redirectUrl, setRedirectURL] = useState<string | null>(null);

    //modal
    const [showModal, toggleModal] = useState<boolean>(false);
    const [modalTitle, setModalTitle] = useState<string>('');
    const [modalMessage, setModalMessage] = useState<string>('');
    const [modalConfirmText, setModalConfirmText] = useState<string>('OK');
    const [modalVisibleConfirmBtn, setModalComfirmVisibility] = useState<boolean>(false);
    const [modalVisibleCancelBtn] = useState<boolean>(true);

    //refs
    const cloudSelectEleRef = createRef<CSIFRef>();
    const nodeTypeEleRef = createRef<NTIFRef>();
    const nodesEleRef = createRef<NIFRef>();

    /**
     * To get the cost by code from the config
     * @param code
     * @returns number
     */
    const getCostByCode = (code: string) => {
        if (protocolData && protocolData.addOnCodes) {
            const priceChart = protocolData.addOnCodes;
            const isCodePresent = priceChart.find((o) => o.code === code);
            if (isCodePresent) {
                return isCodePresent?.price;
            }
            return 0;
        }
        return 0;
    };

    /**
     * To calculate the single node cost
     * @returns void
     */
    const calculateNodeCost = () => {
        if (protocolData?.plan?.price === undefined ) {
            // setComponentError('Failed to get plan details.');
            return;
        }

        setComponentError(null);

        let cloudCost = 0;
        let nodeTypeCost = 0;

        if (cloudSelectEleRef.current) cloudCost = getCostByCode(cloudSelectEleRef.current.getSelectedCloud());
        if (nodeTypeEleRef.current) nodeTypeCost = getCostByCode(nodeTypeEleRef.current.getNodeType());

        setNodeCost(() => {
            return protocolData.plan.price + cloudCost + nodeTypeCost;
        });
    };

    /**Will run the code on component mount and update lifecycle */
    useEffect(() => {
        loadPlanDetails();
    }, []);

    /**Will run the code on change in selected cloud */
    useEffect(() => {
        calculateNodeCost();
    }, [protocolData, selectedCloud, nodeCount, nodeType]);

    /**
     * To get the plan details form th ebackend
     * @returns void
     */
    const loadPlanDetails = async () => {
        setLoading(true);
        setComponentError(null);

        try {
            const response: any = await getPlanData();
            // const response: any = await (await axios.get('http://localhost:3001/data')).data;

            if (response && response.data) {
                setProtocolData(response.data);
                setNodeCost(response.data.plan.price);
                setComponentError(null);
            } else {
                setNodeCost(0);
                setComponentError('Failed to get plan details. Please try again later.');
            }
            // eslint-disable-next-line
        } catch (err: any) {
            setComponentError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const checkStakingOrDedicated = (): string => {
        if (window.location.pathname.includes('full')) {
          return 'dedicatedNodes';
        }
        return 'stakingNodes';
    };

    /** Event handler [start] */

    const onPurchase = async () => {
        if (componentError) return;

        if (!cloudSelectEleRef.current?.isValid() || !nodesEleRef.current?.isValid()) {
            setComponentError('Failed to purchase node');
            return;
        }

        const codesArr = [];

        const cloud = cloudSelectEleRef.current.getSelectedCloud();
        if (cloud) codesArr.push(cloud);

        const node = nodeTypeEleRef.current?.getNodeType();
        if (node) codesArr.push(node);

        const payload = {
            quantity: nodesEleRef.current.getQuantity(),
            addOnCodes: codesArr,
            planCode: protocolData.plan.code,
        };

        try {
            const typeOfNode: string = checkStakingOrDedicated();
            const response: any = await createSubscription(payload, typeOfNode);

            if (response) {
                toggleModal(true);
                setModalTitle('Purchasing Shardeum');
                if (response.success) {
                    setModalMessage(response.data.message || '');
                    setRedirectURL(response.data.result.redirectionUrl);
                    setModalComfirmVisibility(true);
                    setModalConfirmText('Continue');
                    setComponentError(null);
                } else if (response.error) {
                    setModalComfirmVisibility(false);
                    if (response.message) setModalMessage(response.message);
                    if (response.error.reason) setModalMessage(response.error.reason);
                    if (response.error.details) setModalMessage(response.error.details);
                }
            } else {
                toggleModal(false);
                setComponentError('Error request failed');
            }
            // eslint-disable-next-line
        } catch (error: any) {
            setComponentError(error.message);
        }
    };

    const onModalConfirm = () => {
        if (!redirectUrl) return;
        window.open(redirectUrl, '_self');
    };

    /** Event handler [end] */

    return (
        <section className='px-5 col-12'>
            {!isLoading && (
                <>
                    <div className='mt-5 col-11 mx-auto'>
                        <div className='col-4'>
                            <img src={Shardeumlogo} alt='shardeum-logo' width='130px' />
                        </div>
                        <div className='mt-3'>
                            <Breadcrumb
                                prefixLink={breadcrumbPath}
                                activeLink="Purchase"
                                textSize={13}
                            />
                        </div>
                        <h5 className='mt-5 fw-bold'>Select Subscription Options</h5>
                        <div className='row'>
                            <div className='col-xl-8 col-lg-8 col-sm-12 mx-auto'>
                                <div className='mt-3'>
                                    <CloudSelect
                                        ref={cloudSelectEleRef}
                                        selectedCloud={selectedCloud}
                                        setSelectedCloud={setSelectedCloud}
                                    />
                                </div>
                                <div className='mt-4 col-12'>
                                    <NodeType
                                        ref={nodeTypeEleRef}
                                        type={purchaseType}
                                        nodeType={nodeType}
                                        selectedCloud={selectedCloud}
                                        setNodeType={setNodeType}
                                    />
                                </div>
                                {componentError && (
                                    <div className='alert-danger mt-4 rounded text-center'>
                                        <p className='text-danger  py-2'>{componentError}</p>
                                    </div>
                                )}
                            </div>
                            <div className='col-xl-4 col-lg-4 col-sm-6 col-md-4 mt-4'>
                                <Nodes
                                    ref={nodesEleRef}
                                    price={nodeCost}
                                    count={nodeCount}
                                    setCount={setNodeCount}
                                    onSubmit={onPurchase}
                                />
                            </div>
                        </div>
                    </div>
                </>
            )}

            {isLoading && <Loader />}

            <Modal
                show={showModal}
                onHide={() => {
                    toggleModal(false);
                    setModalTitle('');
                    setModalMessage('');
                }}
                title={modalTitle}
                visibleFooter
                visibleHeaderCloseIcon={false}
                visibleFooterCloseButton={modalVisibleCancelBtn}
                visibleConfirmBtn={modalVisibleConfirmBtn}
                confirmBtnText={modalConfirmText}
                confirmBtnClassName=''
                onConfirm={onModalConfirm}
            >
                <p className='status text-center mt-3'>{modalMessage}</p>
            </Modal>
        </section>
    );
};

export default PurchaseNode;
